import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { ClassKey } from '@/features/learn/types/video.types'
import type { WatchProgress, ClassProgress, OverallProgress } from '@/features/learn/types/progress.types'

interface UserProgress {
    watchedVideos: Record<string, WatchProgress>
    classProgress: Record<ClassKey, ClassProgress>
    overallProgress: OverallProgress
}

interface LegacyClassProgress {
    videosWatched?: number
    totalWatchTime?: number
    totalVideos?: number
    completionPercentage?: number
}

interface LearnState {
    // User progress data (keyed by userId for multi-user support)
    userProgress: Record<string, UserProgress>

    // UI state
    isLoading: boolean
    error: string | null

    // App lifecycle
    rehydrationComplete: boolean
}

const defaultOverallProgress: OverallProgress = {
    totalVideosWatched: 0,
    totalVideos: 0,
    completionPercentage: 0,
    totalWatchTime: 0,
}

const createDefaultUserProgress = (): UserProgress => ({
    watchedVideos: {},
    classProgress: {} as Record<ClassKey, ClassProgress>,
    overallProgress: defaultOverallProgress,
})

const initialState: LearnState = {
    userProgress: {},
    isLoading: false,
    error: null,
    rehydrationComplete: false,
}

const learnSlice = createSlice({
    name: 'learn',
    initialState,
    reducers: {
        // Pure state updates only - no business logic
        setVideoProgress: (
            state,
            action: PayloadAction<{
                userId: string
                videoId: string
                progress: Partial<WatchProgress>
            }>,
        ) => {
            const { userId, videoId, progress } = action.payload

            if (!state.userProgress[userId]) {
                state.userProgress[userId] = createDefaultUserProgress()
            }

            const existingProgress = state.userProgress[userId].watchedVideos[videoId] || {
                lastWatched: new Date().toISOString(),
                watchCount: 0,
                maxProgress: 0,
                totalWatchTime: 0,
            }

            state.userProgress[userId].watchedVideos[videoId] = {
                ...existingProgress,
                ...progress,
                lastWatched: new Date().toISOString(),
            }
        },

        setClassProgress: (
            state,
            action: PayloadAction<{
                userId: string
                classKey: ClassKey
                progress: ClassProgress
            }>,
        ) => {
            const { userId, classKey, progress } = action.payload

            if (!state.userProgress[userId]) {
                state.userProgress[userId] = createDefaultUserProgress()
            }

            state.userProgress[userId].classProgress[classKey] = progress
        },

        setOverallProgress: (
            state,
            action: PayloadAction<{
                userId: string
                progress: OverallProgress
            }>,
        ) => {
            const { userId, progress } = action.payload

            if (!state.userProgress[userId]) {
                state.userProgress[userId] = createDefaultUserProgress()
            }

            state.userProgress[userId].overallProgress = progress
        },

        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
            state.isLoading = false
        },

        clearUserProgress: (state, action: PayloadAction<string>) => {
            delete state.userProgress[action.payload]
        },

        clearAllProgress: (state) => {
            state.userProgress = {}
        },

        rehydrationComplete: (state) => {
            state.rehydrationComplete = true
            state.isLoading = false
        },

        // Migration helper: import legacy localStorage data
        importLegacyData: (
            state,
            action: PayloadAction<{
                userId: string
                legacyData: {
                    watched: Record<string, WatchProgress>
                    progressByClass: Record<string, LegacyClassProgress>
                }
            }>,
        ) => {
            const { userId, legacyData } = action.payload

            if (!state.userProgress[userId]) {
                state.userProgress[userId] = createDefaultUserProgress()
            }

            // Import watched videos
            state.userProgress[userId].watchedVideos = legacyData.watched || {}

            // Import class progress (will be recalculated properly by services)
            // Store the legacy data but mark it for recalculation
            Object.entries(legacyData.progressByClass || {}).forEach(([classKey, progress]) => {
                if (progress) {
                    state.userProgress[userId].classProgress[classKey as ClassKey] = {
                        videosWatched: progress.videosWatched || 0,
                        totalWatchTime: progress.totalWatchTime || 0,
                        totalVideos: progress.totalVideos || 0,
                        completionPercentage: progress.completionPercentage || 0,
                    }
                }
            })
        },
    },
})

export const {
    setVideoProgress,
    setClassProgress,
    setOverallProgress,
    setLoading,
    setError,
    clearUserProgress,
    clearAllProgress,
    rehydrationComplete,
    importLegacyData,
} = learnSlice.actions

export const learnReducer = learnSlice.reducer
export { learnSlice }

// Export types for use in selectors and services
export type { LearnState, UserProgress }
